import Dropdown, {Anchor} from '@jetbrains/ring-ui/components/dropdown/dropdown'
import Loader from '@jetbrains/ring-ui/components/loader/loader'
import Popup from '@jetbrains/ring-ui/components/popup/popup'
import {Directions} from '@jetbrains/ring-ui/components/popup/popup.consts'
import {memo} from 'react'

import Measure from '../../../../containers/Measure'
import {useBranch} from '../../../../hooks/routes'
import {restApi} from '../../../../services/rest'
import SequenceLoader from '../../../common/SequenceLoader/SequenceLoader'
import {useTestOccurencesArg} from '../Tests.rest'

import TestHistoryChart from './TestHistoryChart.lazy'
import type {PopupContainerProps} from './TestHistoryChart.types'
import {getLocator} from './TestHistoryChart.utils'

import styles from './TestHistoryChart.css'

const POPUP_DIRECTIONS = [
  Directions.BOTTOM_LEFT,
  Directions.BOTTOM_RIGHT,
  Directions.TOP_LEFT,
  Directions.TOP_RIGHT,
]

function TestHistoryChartContainerPopup({anchor, ...props}: PopupContainerProps) {
  const branch = useBranch()
  const locator = getLocator(props.testId, props.projectId, props.buildTypeId, branch)
  const arg = useTestOccurencesArg(locator)
  const {ready, hasTestOccurrences} =
    restApi.endpoints.getAllTestOccurrencesNormalized.useQueryState(arg, {
      selectFromResult: ({data}) => ({
        ready: data != null,
        hasTestOccurrences: Boolean(data?.data.result.length),
      }),
    })

  const renderAnchor = (
    <Anchor className={styles.anchor} title="View trend">
      {anchor}
    </Anchor>
  )
  const renderLoader = (
    <div className={styles.loader}>
      <Loader />
      <span className={styles.loaderLabel}>{'Test duration is loading'}</span>
    </div>
  )
  return (
    <Measure client>
      {({measureRef, contentRect}) => {
        const width = contentRect.client?.width
        return (
          <Dropdown className={styles.dropdown} anchor={renderAnchor}>
            <Popup className={styles.popup} directions={POPUP_DIRECTIONS}>
              <div ref={measureRef} />
              {width != null ? (
                <SequenceLoader loader={renderLoader}>
                  <TestHistoryChart
                    {...props}
                    className={styles.popupChart}
                    seqKey="TestHistoryChart"
                    width={width}
                    expandable={false}
                  />
                </SequenceLoader>
              ) : (
                renderLoader
              )}
              {width != null && ready && !hasTestOccurrences ? (
                <div className={styles.fewLaunches}>
                  {'Test run statistics is displayed here if the test runs more than once'}
                </div>
              ) : null}
            </Popup>
          </Dropdown>
        )
      }}
    </Measure>
  )
}

export default memo<PopupContainerProps>(TestHistoryChartContainerPopup)
